import React, { useState } from "react";
import {
  ArrowRightLeft, AlertTriangle, CheckCircle2, Loader2, Send,
  ShieldAlert, ChevronRight, Database, Trash2, Copy as CopyIcon, X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/**
 * Super Admin — Cross-deployment tenant migration.
 *
 * Copies a tenant's entire dataset (tenant doc + all tenant-scoped
 * collections) from a SOURCE deployment into THIS deployment.
 *
 * Flow:
 *   1. Operator enters source URL, source super-admin phone, tenant
 *      slug(s). Clicks "Send OTP to source" — that calls the source
 *      deployment's /api/auth/otp/request over the network.
 *   2. Operator reads the OTP off the source's super-admin phone and
 *      pastes it back into the form.
 *   3. Operator clicks "Check" — target-side calls
 *      /api/superadmin/tenants/migrate/check which authenticates with
 *      the source, resolves the source tenant, and inspects the target.
 *      Returns whether target exists + row counts.
 *   4. UI shows: "Target has X rows. Merge, wipe, or cancel?"
 *   5. Operator picks a mode + clicks Migrate. Optionally with dry-run.
 */
export default function SuperAdminMigrateTenant() {
  const [form, setForm] = useState({
    source_backend_url: "",
    source_super_admin_phone: "",
    source_super_admin_otp: "",
    source_tenant_slug: "",
    target_tenant_slug: "",
  });
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const [mode, setMode] = useState("create"); // create | merge | wipe_and_copy
  const [confirmWipe, setConfirmWipe] = useState("");
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const reset = () => {
    setCheckResult(null);
    setResult(null);
    setMode("create");
    setConfirmWipe("");
  };

  // --- 1. Request OTP on the source deployment ---
  const sendOtp = async () => {
    if (!form.source_backend_url || !form.source_super_admin_phone) {
      toast.error("Enter source URL + super-admin phone first");
      return;
    }
    setSendingOtp(true);
    try {
      const base = form.source_backend_url.replace(/\/$/, "");
      const res = await fetch(`${base}/api/auth/otp/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: form.source_super_admin_phone }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Source OTP request failed (${res.status}): ${txt.slice(0, 200)}`);
      }
      setOtpSent(true);
      toast.success(`OTP sent to ${form.source_super_admin_phone} on source`);
    } catch (e) {
      toast.error(e.message || "Failed to send OTP");
    } finally {
      setSendingOtp(false);
    }
  };

  // --- 2. Check target ---
  const check = async () => {
    reset();
    const target = (form.target_tenant_slug || form.source_tenant_slug).trim();
    if (!form.source_backend_url || !form.source_super_admin_phone
        || !form.source_super_admin_otp || !form.source_tenant_slug) {
      toast.error("Fill in all required fields (source URL, phone, OTP, source slug)");
      return;
    }
    setChecking(true);
    try {
      const { data } = await api.post("/superadmin/tenants/migrate/check", {
        source_backend_url: form.source_backend_url.trim(),
        source_super_admin_phone: form.source_super_admin_phone.trim(),
        source_super_admin_otp: form.source_super_admin_otp.trim(),
        source_tenant_slug: form.source_tenant_slug.trim(),
        target_tenant_slug: target,
      });
      setCheckResult(data);
      // Default mode: 'create' if target doesn't exist, else force operator to pick
      setMode(data.target_exists ? "" : "create");
      toast.success(data.target_exists
        ? `Target '${target}' exists with ${data.target_total_rows} rows — choose a mode`
        : `Target '${target}' doesn't exist — will be created`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Check failed");
    } finally {
      setChecking(false);
    }
  };

  // --- 3. Migrate ---
  const migrate = async () => {
    if (!checkResult) return;
    if (!mode) {
      toast.error("Pick a mode first");
      return;
    }
    if (mode === "wipe_and_copy" && confirmWipe !== checkResult.target_tenant_slug) {
      toast.error(`Type "${checkResult.target_tenant_slug}" to confirm wipe`);
      return;
    }
    setMigrating(true);
    setResult(null);
    try {
      const { data } = await api.post("/superadmin/tenants/migrate", {
        source_backend_url: form.source_backend_url.trim(),
        source_tenant_slug: form.source_tenant_slug.trim(),
        target_tenant_slug: checkResult.target_tenant_slug,
        source_token: checkResult.source_token,
        dry_run: dryRun,
        mode,
      });
      setResult(data);
      toast.success(dryRun
        ? `Dry-run: would import ${data.total_rows} rows`
        : `Migrated ${data.total_inserted} rows into '${data.target_tenant_slug}'`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration failed");
    } finally {
      setMigrating(false);
    }
  };

  const targetSlugPreview = (form.target_tenant_slug || form.source_tenant_slug || "…").trim();

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="migrate-tenant-page">
      <div className="flex items-start gap-4">
        <div className="p-3 rounded-xl bg-indigo-50 text-indigo-600">
          <ArrowRightLeft className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Migrate Tenant</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Copy a tenant&apos;s entire dataset (users, orders, products, wallet,
            CMS, push tokens, everything) from another deployment into this
            one. Media URLs are left pointing at the source&apos;s storage.
          </p>
        </div>
      </div>

      {/* Step 1: source form */}
      <section className="border rounded-2xl bg-white shadow-sm divide-y">
        <div className="p-5">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-500 uppercase tracking-wide">
            <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs flex items-center justify-center">1</span>
            Source deployment
          </div>
        </div>
        <div className="p-5 space-y-4">
          <Field
            label="Source backend URL"
            placeholder="https://source.example.com"
            value={form.source_backend_url}
            onChange={set("source_backend_url")}
            testid="src-url"
          />
          <div className="grid md:grid-cols-2 gap-4">
            <Field
              label="Source super-admin phone"
              placeholder="9999999999"
              value={form.source_super_admin_phone}
              onChange={set("source_super_admin_phone")}
              testid="src-phone"
            />
            <div>
              <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide">
                OTP (relayed from source phone)
              </label>
              <div className="mt-1 flex gap-2">
                <input
                  type="text"
                  value={form.source_super_admin_otp}
                  onChange={set("source_super_admin_otp")}
                  placeholder="123456"
                  className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  data-testid="src-otp"
                />
                <button
                  onClick={sendOtp}
                  disabled={sendingOtp || !form.source_backend_url || !form.source_super_admin_phone}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm hover:bg-slate-50 disabled:opacity-50"
                  data-testid="send-otp-btn"
                >
                  {sendingOtp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {otpSent ? "Resend" : "Send OTP"}
                </button>
              </div>
              {otpSent && (
                <p className="mt-1 text-xs text-emerald-600">OTP sent — check the source super-admin phone.</p>
              )}
            </div>
          </div>
          <Field
            label="Source tenant slug"
            placeholder="acme"
            value={form.source_tenant_slug}
            onChange={set("source_tenant_slug")}
            testid="src-slug"
          />
        </div>
      </section>

      {/* Step 2: target form */}
      <section className="border rounded-2xl bg-white shadow-sm divide-y">
        <div className="p-5">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-500 uppercase tracking-wide">
            <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs flex items-center justify-center">2</span>
            Target tenant (this deployment)
          </div>
        </div>
        <div className="p-5">
          <Field
            label="Target tenant slug"
            placeholder={`Leave empty to reuse "${form.source_tenant_slug || 'source slug'}"`}
            value={form.target_tenant_slug}
            onChange={set("target_tenant_slug")}
            testid="tgt-slug"
            hint={targetSlugPreview !== (form.source_tenant_slug || "").trim()
              ? `→ Will create/update: ${targetSlugPreview}`
              : "Falls back to the source slug if empty."}
          />
        </div>
      </section>

      {/* Step 3: check */}
      <div className="flex items-center gap-3">
        <button
          onClick={check}
          disabled={checking}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
          data-testid="check-target-btn"
        >
          {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
          Check target
        </button>
        <span className="text-xs text-slate-500">Validates source credentials + reports what lives on target.</span>
      </div>

      {/* Step 4: mode selection + result */}
      {checkResult && (
        <section className="border rounded-2xl bg-white shadow-sm p-5 space-y-4" data-testid="check-result">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm font-semibold text-slate-900">
                Target: <span className="font-mono">{checkResult.target_tenant_slug}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Source tenant id: <span className="font-mono">{checkResult.source_tenant_id}</span>
              </div>
            </div>
            <button
              onClick={reset}
              className="p-1 rounded-lg hover:bg-slate-100"
              data-testid="reset-check-btn"
            >
              <X className="w-4 h-4 text-slate-500" />
            </button>
          </div>

          {!checkResult.target_exists ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <div className="font-medium text-emerald-900">Target doesn&apos;t exist yet.</div>
                <p className="text-emerald-800 mt-1">
                  A new tenant will be created from the source&apos;s config (branding, theme, categories),
                  minus deployment-scoped fields (Razorpay keys, Firebase shard, S3 creds).
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <div className="font-medium text-amber-900">Target already has data.</div>
                  <p className="text-amber-800 mt-1">
                    {checkResult.target_total_rows} rows across {Object.keys(checkResult.target_row_counts).length} collections.
                    Pick how to handle the conflict:
                  </p>
                </div>
              </div>

              <div className="grid gap-2">
                <ModeOption
                  value="merge"
                  active={mode === "merge"}
                  onSelect={() => setMode("merge")}
                  title="Merge (safe)"
                  desc="Insert new rows. Skip any row whose id already exists on target. Existing target data is preserved."
                  testid="mode-merge"
                />
                <ModeOption
                  value="wipe_and_copy"
                  active={mode === "wipe_and_copy"}
                  onSelect={() => setMode("wipe_and_copy")}
                  title="Wipe and copy (destructive)"
                  desc={`Delete ALL ${checkResult.target_total_rows} rows on target, then copy source data cleanly. Irreversible.`}
                  danger
                  testid="mode-wipe"
                />
              </div>

              {mode === "wipe_and_copy" && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 space-y-2">
                  <div className="flex items-center gap-2 text-red-900 font-medium text-sm">
                    <ShieldAlert className="w-4 h-4" />
                    Confirm destructive wipe
                  </div>
                  <p className="text-xs text-red-800">
                    Type <span className="font-mono font-semibold">{checkResult.target_tenant_slug}</span> below
                    to enable the Migrate button.
                  </p>
                  <input
                    type="text"
                    value={confirmWipe}
                    onChange={(e) => setConfirmWipe(e.target.value)}
                    className="w-full rounded-lg border border-red-300 bg-white px-3 py-2 text-sm"
                    placeholder="Type target slug exactly"
                    data-testid="confirm-wipe-input"
                  />
                </div>
              )}

              {Object.keys(checkResult.target_row_counts).length > 0 && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-slate-600 hover:text-slate-900 text-xs font-medium">
                    View target row counts ({Object.keys(checkResult.target_row_counts).length} collections)
                  </summary>
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs font-mono">
                    {Object.entries(checkResult.target_row_counts)
                      .sort(([, a], [, b]) => b - a)
                      .map(([k, v]) => (
                        <div key={k} className="flex justify-between border-b border-slate-100 py-0.5">
                          <span className="text-slate-600 truncate">{k}</span>
                          <span className="text-slate-900">{v.toLocaleString()}</span>
                        </div>
                      ))}
                  </div>
                </details>
              )}
            </>
          )}

          <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
            <label className="flex items-center gap-2 text-xs text-slate-600 select-none">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
                className="rounded border-slate-300"
                data-testid="dry-run-checkbox"
              />
              Dry-run (count only, no writes)
            </label>
            <div className="flex-1" />
            <button
              onClick={migrate}
              disabled={migrating
                || !mode
                || (mode === "wipe_and_copy" && confirmWipe !== checkResult.target_tenant_slug)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 ${
                mode === "wipe_and_copy"
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : "bg-indigo-600 text-white hover:bg-indigo-700"
              }`}
              data-testid="migrate-btn"
            >
              {migrating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : mode === "wipe_and_copy" ? (
                <Trash2 className="w-4 h-4" />
              ) : (
                <CopyIcon className="w-4 h-4" />
              )}
              {dryRun ? "Simulate" : mode === "wipe_and_copy" ? "Wipe + Migrate" : "Migrate"}
            </button>
          </div>
        </section>
      )}

      {/* Step 5: result */}
      {result && (
        <section
          className={`border rounded-2xl shadow-sm p-5 space-y-3 ${
            result.dry_run
              ? "bg-slate-50 border-slate-200"
              : "bg-emerald-50 border-emerald-200"
          }`}
          data-testid="migrate-result"
        >
          <div className="flex items-center gap-2">
            {result.dry_run ? (
              <Database className="w-5 h-5 text-slate-600" />
            ) : (
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            )}
            <div className="font-semibold text-slate-900">
              {result.dry_run
                ? `Dry-run: would import ${(result.total_rows || 0).toLocaleString()} rows`
                : `Migrated ${(result.total_inserted || 0).toLocaleString()} rows`}
              {result.target_created && !result.dry_run && (
                <span className="text-xs text-emerald-700 ml-2">(target tenant created)</span>
              )}
              {result.total_skipped > 0 && (
                <span className="text-xs text-slate-600 ml-2">
                  ({result.total_skipped.toLocaleString()} skipped as duplicates)
                </span>
              )}
            </div>
          </div>
          <div className="text-xs text-slate-700 space-y-0.5 font-mono">
            <div>mode: <span className="font-semibold">{result.mode}</span></div>
            <div>target_slug: <span className="font-semibold">{result.target_tenant_slug}</span></div>
            <div>target_tenant_id: <span className="font-semibold">{result.target_tenant_id}</span></div>
          </div>

          {result.deleted && Object.keys(result.deleted).length > 0 && (
            <details>
              <summary className="cursor-pointer text-xs font-medium text-red-700">
                Wiped {Object.values(result.deleted).reduce((a, b) => a + b, 0).toLocaleString()} rows
              </summary>
              <CountsGrid counts={result.deleted} />
            </details>
          )}

          {result.platform_dependencies_imported && (
            (result.platform_dependencies_imported.subscription_plans?.length > 0
              || result.platform_dependencies_imported.feature_flags?.length > 0) && (
              <details open>
                <summary className="cursor-pointer text-xs font-medium text-indigo-700">
                  Imported platform dependencies (plans + feature flags this tenant needs)
                </summary>
                <div className="mt-2 text-xs font-mono space-y-0.5">
                  {result.platform_dependencies_imported.subscription_plans?.map((s) => (
                    <div key={`plan-${s}`}>subscription_plans: <span className="font-semibold">{s}</span></div>
                  ))}
                  {result.platform_dependencies_imported.feature_flags?.map((f) => (
                    <div key={`flag-${f}`}>feature_flags: <span className="font-semibold">{f}</span></div>
                  ))}
                </div>
              </details>
            )
          )}

          <details open>
            <summary className="cursor-pointer text-xs font-medium text-slate-700">
              Per-collection {result.dry_run ? "would-import" : "import"} counts
            </summary>
            <CountsGrid counts={result.dry_run ? result.would_import : result.inserted} />
          </details>
        </section>
      )}
    </div>
  );
}

function Field({ label, hint, testid, ...rest }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide">
        {label}
      </label>
      <input
        {...rest}
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:ring-1 focus:ring-slate-500 outline-none"
        data-testid={testid}
      />
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

function ModeOption({ active, onSelect, title, desc, danger, testid }) {
  return (
    <button
      onClick={onSelect}
      className={`text-left rounded-xl border p-3 transition-colors ${
        active
          ? danger
            ? "border-red-400 bg-red-50"
            : "border-indigo-400 bg-indigo-50"
          : "border-slate-200 hover:border-slate-300 bg-white"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-900">{title}</span>
        {active && <ChevronRight className="w-4 h-4 text-slate-500" />}
      </div>
      <p className="text-xs text-slate-600 mt-1">{desc}</p>
    </button>
  );
}

function CountsGrid({ counts }) {
  const entries = Object.entries(counts || {}).sort(([, a], [, b]) => b - a);
  if (!entries.length) return <p className="text-xs text-slate-500 mt-2">No rows.</p>;
  return (
    <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs font-mono">
      {entries.map(([k, v]) => (
        <div key={k} className="flex justify-between border-b border-slate-100 py-0.5">
          <span className="text-slate-600 truncate">{k}</span>
          <span className="text-slate-900">{v.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}
